/**
 * ReportBreachCost.js — Estimated Breach Cost Breakdown
 * Placed before Recommendations — for security teams, not C-suite weekly
 */
import React, { useMemo } from "react";
import { INDUSTRY_COSTS, estimateBreachCostBreakdown } from "../reportUtils";

export default function ReportBreachCost({ stats, theme, SEVERITY_COLORS: SC, industry = "global" }) {
  if (!stats) return null;

  const costBreakdown = useMemo(() =>
    estimateBreachCostBreakdown(stats, industry), [stats, industry]);

  if (!costBreakdown) return null;

  const totalCost = costBreakdown.components.reduce((s, c) => s + c.cost, 0);

  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#C62828">Breach Cost Analysis</SectionTitle>

      {/* Header */}
      <div className="report-card" style={{ ...cardStyle(theme), border: `1px solid ${SC.critical}33`, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <div>
            <SubTitle theme={theme}>ESTIMATED BREACH COST — ITEMIZED BREAKDOWN</SubTitle>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>
              Industry: <strong style={{ color: theme.colors.textLabel }}>{costBreakdown.industry}</strong> ·
              Volume: <strong style={{ color: theme.colors.textLabel }}>{costBreakdown.volumeLabel}</strong> ·
              Multiplier: <strong style={{ color: SC.critical }}>{costBreakdown.volumeMult}×</strong>
            </div>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>
              {costBreakdown.source}
            </div>
            <div style={{ marginTop: 8, padding: "6px 12px", background: "rgba(245,127,23,0.08)", border: "1px solid rgba(245,127,23,0.3)", borderRadius: 6, fontFamily: "monospace", fontSize: 11, color: "#F57F17", maxWidth: 520 }}>
              ⚠ This is a benchmark estimate for security planning purposes — not a guaranteed or precise financial prediction.
              Actual costs depend on organization size, security maturity, regulatory jurisdiction, and breach scope.
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1 }}>TOTAL ESTIMATE</div>
            <div style={{ fontFamily: "monospace", fontSize: 32, fontWeight: 900, color: SC.critical }}>${totalCost.toLocaleString()}</div>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>USD — Estimate only</div>
          </div>
        </div>

        {/* Cost Table */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
          <thead>
            <tr style={{ background: theme.colors.tableHeader }}>
              {["","Cost Component","Estimated Cost","% of Total","Source","Notes"].map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `2px solid ${theme.colors.cardBorder}`, fontSize: 11, letterSpacing: 1 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {costBreakdown.components.map((c, i) => {
              const pct = totalCost > 0 ? ((c.cost / totalCost) * 100).toFixed(1) : "—";
              return (
                <tr key={i} style={{ background: i%2===0 ? theme.colors.rowAlt : theme.colors.card, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                  <td style={{ padding: "8px 10px", fontSize: 16 }}>{c.icon}</td>
                  <td style={{ padding: "8px 10px", color: theme.colors.textPrimary, fontWeight: 700 }}>{c.label}</td>
                  <td style={{ padding: "8px 10px", color: SC.critical, fontWeight: 900, fontSize: 13 }}>${c.cost.toLocaleString()}</td>
                  <td style={{ padding: "8px 10px", color: theme.colors.textMuted }}>{c.pct !== null ? `${c.pct}%` : `${pct}%`}</td>
                  <td style={{ padding: "8px 10px" }}>
                    <span style={{ padding: "2px 6px", background: `${theme.colors.accent}18`, border: "1px solid #1565C020", borderRadius: 4, color: theme.colors.accent, fontSize: 11, fontWeight: 700 }}>{c.source}</span>
                  </td>
                  <td style={{ padding: "8px 10px", color: theme.colors.textMuted, fontSize: 11 }}>{c.note}</td>
                </tr>
              );
            })}
            <tr style={{ background: `${SC.critical}08`, borderTop: `2px solid ${theme.colors.cardBorder}` }}>
              <td style={{ padding: "10px" }}>💰</td>
              <td style={{ padding: "10px", fontWeight: 900, color: "#FFD54F", fontSize: 12 }}>TOTAL ESTIMATED BREACH COST</td>
              <td style={{ padding: "10px", fontWeight: 900, color: SC.critical, fontSize: 16 }}>${totalCost.toLocaleString()}</td>
              <td style={{ padding: "10px", color: theme.colors.textMuted }}>100%</td>
              <td colSpan={2} style={{ padding: "10px", fontSize: 11, color: theme.colors.textMuted }}>
                Estimate — actual costs vary by org size, security posture, regulatory jurisdiction
              </td>
            </tr>
          </tbody>
        </table>

        {/* Industry Comparison Bars */}
        <div style={{ marginTop: 14, padding: "14px 16px", background: theme.colors.inputBg, borderRadius: 8 }}>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 10 }}>
            IBM 2024 — AVERAGE BREACH COST BY INDUSTRY
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            {Object.entries(INDUSTRY_COSTS).filter(([k]) => k !== "global").map(([key, ind]) => {
              const maxCost  = 9770000;
              const barH     = Math.round((ind.cost / maxCost) * 70);
              const isActive = key === industry;
              return (
                <div key={key} style={{ textAlign: "center", flex: 1, minWidth: 50 }}>
                  {isActive && (
                    <div style={{ fontFamily: "monospace", fontSize: 11, color: SC.critical, marginBottom: 2, fontWeight: 700 }}>
                      ${(totalCost/1000000).toFixed(1)}M
                    </div>
                  )}
                  <div style={{
                    height: barH,
                    background: isActive ? SC.critical : "#BDBDBD",
                    borderRadius: "3px 3px 0 0",
                    marginBottom: 4,
                    border: isActive ? `2px solid ${SC.critical}` : "none"
                  }} />
                  <div style={{ fontFamily: "monospace", fontSize: 11, color: isActive ? SC.critical : theme.colors.textMuted, fontWeight: isActive ? 900 : 400 }}>
                    {ind.label.split(" ")[0]}
                  </div>
                  <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>
                    ${(ind.cost/1000000).toFixed(1)}M
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

const cardStyle = (theme) => ({
  background: theme.colors.card,
  border: `1px solid ${theme.colors.cardBorder}`,
  borderRadius: 10, padding: 20,
});
const SectionTitle = ({ children, color = "#C62828" }) => (
  <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color, letterSpacing: 2, textTransform: "uppercase", borderLeft: `4px solid ${color}`, paddingLeft: 12, marginBottom: 16 }}>{children}</div>
);
const SubTitle = ({ children, theme }) => (
  <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 10, fontWeight: 700 }}>{children}</div>
);
